import datapoints from './datapoints';
import markItemsAsPinnedService from '../services/markItemsAsPinnedService';
import markItemsAsPinnedStoreUpdate from 'owa-mail-actions/lib/triage/markItemsAsPinnedStoreUpdate';
import { getRowIdsFromRowKeys, MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import * as undoActions from 'owa-mail-undo';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { userMailInteractionAction } from 'owa-mail-actions';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { getMailboxRequestOptions } from 'owa-request-options-types';

/**
 * Pin or unpin items
 * @param rowKeys the row keys
 * @param tableView that the pinned/unpinned items are in
 * @param shouldMarkAsPinned the pin value to be set
 * @param actionSource the action source
 */
export default createLegacyActionWithDatapoint(
    'pinItems',
    datapoints.pinItems
)(function pinItems(
    rowKeys: string[],
    tableView: TableView,
    shouldMarkAsPinned: boolean,
    actionSource: ActionSource
): Promise<any> {
    undoActions.clearLastUndoableAction();

    // Get all the row keys to update by checking if the existing pinned value is the same as the one to set
    const filteredRowKeys = filterRowKeysByPinValue(rowKeys, tableView, shouldMarkAsPinned);

    // Get the dedupped item ids to update, as multiple rowKeys could map to the same rowId
    const itemIdStringsToUpdate = getRowIdsFromRowKeys(filteredRowKeys, tableView.id);
    if (itemIdStringsToUpdate.length == 0) {
        // No need to send out the service call
        return Promise.resolve();
    }

    markItemsAsPinnedStoreUpdate(filteredRowKeys, tableView.id, shouldMarkAsPinned);

    if (shouldMarkAsPinned) {
        const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
        userMailInteractionAction('Pin', instrumentationContexts);
    }

    // Make service request
    // Task 658: Reload client state when pin action fails on the server
    const requestInitOptions = getMailboxRequestOptions(getMailboxInfo(tableView));
    return markItemsAsPinnedService(itemIdStringsToUpdate, shouldMarkAsPinned, requestInitOptions);
});

/**
 * Filter the row keys to update by the pin value
 * @param rowKeys to pin/unpin
 * @param tableView that the rows belong to
 * @param shouldMarkAsPinned isPinned value to be set
 */
function filterRowKeysByPinValue(
    rowKeys: string[],
    tableView: TableView,
    shouldMarkAsPinned: boolean
): string[] {
    const filteredRowKeys = [];

    rowKeys.forEach(rowKey => {
        if (
            (MailRowDataPropertyGetter.getIsPinned(rowKey, tableView) && !shouldMarkAsPinned) ||
            (!MailRowDataPropertyGetter.getIsPinned(rowKey, tableView) && shouldMarkAsPinned)
        ) {
            filteredRowKeys.push(rowKey);
        }
    });

    return filteredRowKeys;
}
